import { ChangeEvent, FC, useState, FormEvent } from 'react';
import { Button, Box, Form, Dialog, Input, Radio, Typography } from '@robocorp/components';
import { styled } from '@robocorp/theme';
import { useTranslation } from 'react-i18next';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';

import { uiActions, feedbackActions } from '../actions';
import { AppState } from '~/store';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

const RadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const FeedbackDialog: FC<Props> = ({
  feedbackDialogOpen,
  toggleFeedbackDialog,
  sendFeedback,
  loading,
  error,
  currentUser,
}) => {
  const { t } = useTranslation();
  const [npsScore, setNpsScore] = useState<number | undefined>(undefined);
  const [formValue, changeFormValue] = useState('');
  const [formSubmitted, changeFormSubmited] = useState<boolean>(false);

  const clearForm = () => {
    changeFormSubmited(false);
    changeFormValue('');
    setNpsScore(undefined);
  };

  const setNpsScoreString = (event: ChangeEvent<HTMLInputElement>) => {
    const npsAsNumber = Number(event.target.value);
    if (!Number.isNaN(npsAsNumber)) {
      setNpsScore(npsAsNumber);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();

    const email = currentUser.details.email ?? '';
    sendFeedback(npsScore, email, formValue);

    if (!error) {
      changeFormSubmited(true);
    }
  };

  const onClose = () => {
    toggleFeedbackDialog();
    window.setTimeout(() => clearForm(), 1000);
  };

  const onFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    changeFormValue(e.target.value);
  };

  const isSubmitted = formSubmitted && !loading && !error;

  return (
    <Dialog open={feedbackDialogOpen} onClose={onClose} width={720}>
      <Form onSubmit={onSubmit}>
        <Dialog.Header>
          <Dialog.Header.Title title={t('sendFeedbackTitle')} />
        </Dialog.Header>
        <Dialog.Content>
          {isSubmitted ? (
            <>
              <Box height={272} textAlign="center" display="flex">
                <Box alignSelf="center" margin="0 auto">
                  <Typography>{t('feedbackSent')}</Typography>
                  <Box pt={20}>
                    <Typography>{t('feedbackSentDesc')}</Typography>
                  </Box>
                  <Box pt={80}>
                    <Button variant="secondary" onClick={onClose}>
                      {t('continue')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Form.Fieldset>
                <Box display="inline-block">
                  <Box pb="10px">
                    <Typography>{t('sendFeedbackSubtitle')}</Typography>
                  </Box>
                  <Box>
                    <Input
                      required
                      rows={5}
                      value={formValue}
                      onChange={onFormChange}
                      placeholder={t('feedbackPlaceholder')}
                      aria-label="feedback"
                    />
                  </Box>
                </Box>
              </Form.Fieldset>
              <Form.Fieldset>
                <Box display="inline">
                  <Box pb="$8">
                    <Typography>{t('npsQuestion')}</Typography>
                  </Box>
                  <Box>
                    <RadioGroup value={npsScore?.toString()} onChange={setNpsScoreString}>
                      <Radio label="0" value="0" />
                      <Radio label="1" value="1" />
                      <Radio label="2" value="2" />
                      <Radio label="3" value="3" />
                      <Radio label="4" value="4" />
                      <Radio label="5" value="5" />
                      <Radio label="6" value="6" />
                      <Radio label="7" value="7" />
                      <Radio label="8" value="8" />
                      <Radio label="9" value="9" />
                      <Radio label="10" value="10" />
                    </RadioGroup>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box pt="5px">
                      <Typography variant="body.small" color="content.subtle">
                        {t('npsNotLikely').toUpperCase()}
                      </Typography>
                    </Box>
                    <Box pt="5px">
                      <Typography variant="body.small" color="content.subtle">
                        {t('npsLikely').toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Form.Fieldset>
              <Box>
                {error && (
                  <Box pt={10}>
                    <Typography fontSize="$12" color="red50">
                      {t('errorSendingFeedback')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Dialog.Content>
        {!isSubmitted ? (
          <Dialog.Actions>
            <Button type="submit" loading={loading}>
              {t('submit')}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('cancel')}
            </Button>
          </Dialog.Actions>
        ) : null}
      </Form>
    </Dialog>
  );
};

function mapStateToProps(state: AppState) {
  const { feedbackDialogOpen } = state.ui;
  const { loading, error } = state.entityStates.feedback;

  return {
    feedbackDialogOpen,
    loading,
    error,
    currentUser: state.userResources.users.current,
  };
}

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      toggleFeedbackDialog: uiActions.toggleFeedbackDialog,
      sendFeedback: feedbackActions.sendFeedback,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(FeedbackDialog);
